// auth.interceptor.ts
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError, BehaviorSubject, filter, take } from 'rxjs';
import { AuthService } from '../services/conf/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);

    // URLs que no requieren token
    const publicUrls = ['/auth/login', '/auth/register', '/users/register', '/auth/refresh'];

    // 🔥 CRÍTICO: Verificar con la URL completa
    const isPublicUrl = publicUrls.some((url) => req.url.includes(url));
    if (isPublicUrl) {
        console.log('🌐 Public URL, skipping interceptor:', req.url);
        return next(req);
    }

    // Obtener el access token
    const token = authService.getAccessToken();

    // Si no hay token, continuar sin modificar
    if (!token) {
        return next(req);
    }

    // Clonar la petición y agregar el token
    const clonedReq = req.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`
        }
    });

    // Continuar con la petición y manejar errores
    return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // 🔥 IMPORTANTE: Solo intentar refresh si NO es la URL de refresh
            if (error.status === 401 && !req.url.includes('/auth/refresh')) {
                console.log('🔄 Token expired, attempting refresh...');

                // Si ya estamos refrescando, esperar
                if (isRefreshing) {
                    return refreshTokenSubject.pipe(
                        filter((token) => token !== null),
                        take(1),
                        switchMap((token) => {
                            const retryReq = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${token}`
                                }
                            });
                            return next(retryReq);
                        })
                    );
                }

                isRefreshing = true;
                refreshTokenSubject.next(null);

                return authService.refreshToken().pipe(
                    switchMap(() => {
                        isRefreshing = false;
                        const newToken = authService.getAccessToken();
                        refreshTokenSubject.next(newToken);

                        // Reintentar la petición original con el nuevo token
                        const retryReq = req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${newToken}`
                            }
                        });
                        return next(retryReq);
                    }),
                    catchError((refreshError) => {
                        isRefreshing = false;
                        console.error('❌ Refresh failed, logging out');
                        authService.logout();
                        return throwError(() => refreshError);
                    })
                );
            }

            // Si es 401 en /auth/refresh, hacer logout directamente
            if (error.status === 401 && req.url.includes('/auth/refresh')) {
                console.error('❌ Refresh token invalid, logging out');
                authService.logout();
            }

            return throwError(() => error);
        })
    );
};
